import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    ChevronDown, ChevronUp, CheckCircle, XCircle, Clock, Trash2,
    Calendar, Package, Truck, Wrench, MapPin, Phone, Mail, User,
    ShoppingCart,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../services/api";

const STATUS = {
    pending:   { label: "Čaká",      color: "text-amber-400",   bg: "bg-amber-400/10 border-amber-400/25", dot: "bg-amber-400"  },
    confirmed: { label: "Potvrdená", color: "text-green-400",   bg: "bg-green-400/10 border-green-400/25", dot: "bg-green-400"  },
    cancelled: { label: "Zamietnutá",color: "text-red-400",     bg: "bg-red-400/10 border-red-400/25",     dot: "bg-red-400"    },
};

const SERVICE_LABEL = {
    pickup:         "Vyzdvihnutie na sklade",
    delivery:       "Dovoz a odvoz",
    delivery_setup: "Dovoz + inštalácia + obsluha",
};
const SERVICE_ICON = { pickup: Package, delivery: Truck, delivery_setup: Wrench };

function formatDate(iso) {
    if (!iso) return "—";
    const [y, m, d] = iso.split("-");
    return `${d}.${m}.${y}`;
}

function daysBetween(start, end) {
    if (!start || !end) return 0;
    const s = new Date(start), e = new Date(end);
    return Math.round((e - s) / 86400000) + 1;
}

// ── Booking card ──────────────────────────────────────────────────────────────
function BookingCard({ booking, onStatusChange, onDelete }) {
    const [expanded, setExpanded] = useState(!booking.read && booking.status === "pending");
    const [updating, setUpdating] = useState(false);
    const s = STATUS[booking.status] || STATUS.pending;
    const ServiceIcon = SERVICE_ICON[booking.service_type] || Package;
    const days = daysBetween(booking.start_date, booking.end_date);

    const updateStatus = async (status) => {
        setUpdating(true);
        try {
            await api.updateBookingStatus(booking.id, status);
            onStatusChange(booking.id, status);
            toast.success(status === "confirmed" ? "Rezervácia potvrdená" : "Rezervácia zamietnutá");
        } catch (e) {
            toast.error(e.message);
        } finally {
            setUpdating(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm("Odstrániť rezerváciu?")) return;
        try {
            await api.deleteBooking(booking.id);
            onDelete(booking.id);
            toast.success("Rezervácia odstránená");
        } catch (e) {
            toast.error(e.message);
        }
    };

    const handleExpand = async () => {
        setExpanded(v => !v);
        if (!booking.read) {
            try { await api.markBookingRead(booking.id); onStatusChange(booking.id, booking.status, true); }
            catch {}
        }
    };

    return (
        <div className={`border rounded-xl overflow-hidden transition-colors ${
            !booking.read && booking.status === "pending"
                ? "border-nova-cyan/25 bg-nova-cyan/3"
                : "border-white/8 bg-nova-surface/10"
        }`}>
            {/* Header row */}
            <button onClick={handleExpand} className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-white/3 transition-colors">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    {/* Unread dot */}
                    {!booking.read && booking.status === "pending" && (
                        <div className="w-2 h-2 rounded-full bg-nova-cyan shrink-0 animate-pulse" />
                    )}
                    {/* Avatar */}
                    <div className="w-8 h-8 rounded-full bg-nova-surface border border-white/10 flex items-center justify-center text-xs font-bold text-white shrink-0">
                        {booking.name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-white font-semibold text-sm">{booking.name}</span>
                            <span className={`font-mono text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${s.bg} ${s.color}`}>
                                {s.label}
                            </span>
                            {booking.status === "pending" && !booking.read && (
                                <span className="font-mono text-[9px] uppercase tracking-wider text-nova-cyan">Nová</span>
                            )}
                        </div>
                        <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                            <span className="text-zinc-500 text-xs font-mono">
                                {formatDate(booking.start_date)} → {formatDate(booking.end_date)}
                                {days > 0 && ` (${days} ${days===1?"deň":days<5?"dni":"dní"})`}
                            </span>
                            <span className="text-zinc-600 text-xs flex items-center gap-1">
                                <ServiceIcon className="w-3 h-3" />
                                {SERVICE_LABEL[booking.service_type] || booking.service_type}
                            </span>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <span className="text-zinc-600 text-xs hidden sm:block font-mono">
                        {booking.items?.length || 0} {(booking.items?.length || 0) === 1 ? "položka" : "položky"}
                    </span>
                    {expanded ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
                </div>
            </button>

            {/* Expanded details */}
            <AnimatePresence>
                {expanded && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }} className="overflow-hidden border-t border-white/8">
                        <div className="px-4 py-4 space-y-4">
                            {/* Items */}
                            <div>
                                <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-600 mb-2 flex items-center gap-1.5">
                                    <ShoppingCart className="w-3 h-3" /> Rezervovaná technika
                                </div>
                                <div className="space-y-1.5">
                                    {(booking.items || []).map((item, i) => (
                                        <div key={i} className="flex items-center justify-between bg-nova-black/30 rounded-lg px-3 py-2">
                                            <span className="text-white text-sm">{item.equipment_name}</span>
                                            <div className="flex items-center gap-3">
                                                <span className="text-zinc-500 text-xs font-mono">×{item.qty}</span>
                                                {item.price_per_day > 0 && (
                                                    <span className="text-nova-cyan text-xs font-mono">{item.price_per_day}€/deň</span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {days > 0 && (booking.items||[]).some(i=>i.price_per_day>0) && (
                                    <div className="mt-2 text-right text-xs text-zinc-500">
                                        Orientačná cena celkom:{" "}
                                        <span className="text-white font-semibold">
                                            {((booking.items||[]).reduce((s,i)=>s+i.price_per_day*i.qty,0)*days).toFixed(0)}€
                                        </span>
                                        <span className="text-zinc-600"> ({days} dní)</span>
                                    </div>
                                )}
                            </div>

                            {/* Contact */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-600 mb-1">Kontakt</div>
                                    <div className="flex items-center gap-2 text-sm">
                                        <User className="w-3.5 h-3.5 text-zinc-600 shrink-0" />
                                        <span className="text-white">{booking.name}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm">
                                        <Mail className="w-3.5 h-3.5 text-zinc-600 shrink-0" />
                                        <a href={`mailto:${booking.email}`} className="text-nova-cyan hover:underline truncate">{booking.email}</a>
                                    </div>
                                    {booking.phone && (
                                        <div className="flex items-center gap-2 text-sm">
                                            <Phone className="w-3.5 h-3.5 text-zinc-600 shrink-0" />
                                            <a href={`tel:${booking.phone}`} className="text-zinc-300 hover:text-white">{booking.phone}</a>
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-600 mb-1">Termín & Služba</div>
                                    <div className="flex items-center gap-2 text-sm mt-1">
                                        <Calendar className="w-3.5 h-3.5 text-zinc-600 shrink-0" />
                                        <span className="text-white">{formatDate(booking.start_date)} – {formatDate(booking.end_date)}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm mt-1.5">
                                        <ServiceIcon className="w-3.5 h-3.5 text-zinc-600 shrink-0" />
                                        <span className="text-zinc-300">{SERVICE_LABEL[booking.service_type] || booking.service_type}</span>
                                    </div>
                                    {booking.delivery_address && (
                                        <div className="flex items-start gap-2 text-sm mt-1.5">
                                            <MapPin className="w-3.5 h-3.5 text-zinc-600 shrink-0 mt-0.5" />
                                            <span className="text-zinc-400">{booking.delivery_address}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {booking.message && (
                                <div className="p-3 rounded-lg bg-nova-black/40 border border-white/5">
                                    <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-600 mb-1.5">Poznámka</div>
                                    <p className="text-zinc-300 text-sm leading-relaxed">{booking.message}</p>
                                </div>
                            )}

                            {/* Actions */}
                            <div className="flex items-center gap-2 pt-1">
                                {booking.status === "pending" && (
                                    <>
                                        <button onClick={() => updateStatus("confirmed")} disabled={updating}
                                            className="flex items-center gap-1.5 h-8 px-4 rounded-lg bg-green-500/15 text-green-400 border border-green-500/25 text-xs font-semibold hover:bg-green-500/25 transition-colors disabled:opacity-50">
                                            <CheckCircle className="w-3.5 h-3.5" /> Potvrdiť
                                        </button>
                                        <button onClick={() => updateStatus("cancelled")} disabled={updating}
                                            className="flex items-center gap-1.5 h-8 px-4 rounded-lg bg-red-500/10 text-red-400 border border-red-500/20 text-xs font-semibold hover:bg-red-500/20 transition-colors disabled:opacity-50">
                                            <XCircle className="w-3.5 h-3.5" /> Zamietnuť
                                        </button>
                                    </>
                                )}
                                {booking.status === "confirmed" && (
                                    <button onClick={() => updateStatus("cancelled")} disabled={updating}
                                        className="flex items-center gap-1.5 h-8 px-4 rounded-lg bg-red-500/10 text-red-400 border border-red-500/20 text-xs font-semibold hover:bg-red-500/20 transition-colors disabled:opacity-50">
                                        <XCircle className="w-3.5 h-3.5" /> Zrušiť
                                    </button>
                                )}
                                {booking.status === "cancelled" && (
                                    <button onClick={() => updateStatus("pending")} disabled={updating}
                                        className="flex items-center gap-1.5 h-8 px-4 rounded-lg bg-amber-400/10 text-amber-400 border border-amber-400/20 text-xs font-semibold hover:bg-amber-400/20 transition-colors disabled:opacity-50">
                                        <Clock className="w-3.5 h-3.5" /> Vrátiť do čakania
                                    </button>
                                )}
                                <a href={`mailto:${booking.email}?subject=Rezervácia KB MUSIC — ${booking.start_date}`}
                                    className="flex items-center gap-1.5 h-8 px-4 rounded-lg bg-white/5 text-zinc-400 border border-white/10 text-xs font-semibold hover:text-white transition-colors">
                                    <Mail className="w-3.5 h-3.5" /> Odpovedať
                                </a>
                                <button onClick={handleDelete}
                                    className="ml-auto flex items-center gap-1.5 h-8 px-3 rounded-lg text-zinc-600 hover:text-red-400 hover:bg-red-400/8 transition-colors text-xs">
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function AdminBookings() {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading]   = useState(true);
    const [filter, setFilter]     = useState("all");

    const load = useCallback(() => {
        api.getBookings()
            .then(setBookings)
            .catch(() => toast.error("Chyba pri načítaní"))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleStatusChange = (id, status, markRead = false) => {
        setBookings(prev => prev.map(b =>
            b.id === id ? { ...b, status, ...(markRead ? { read: true } : {}) } : b
        ));
    };

    const handleDelete = (id) => setBookings(prev => prev.filter(b => b.id !== id));

    const counts = {
        all:       bookings.length,
        pending:   bookings.filter(b => b.status === "pending").length,
        confirmed: bookings.filter(b => b.status === "confirmed").length,
        cancelled: bookings.filter(b => b.status === "cancelled").length,
    };
    const newCount = bookings.filter(b => b.status === "pending" && !b.read).length;

    const filtered = filter === "all" ? bookings : bookings.filter(b => b.status === filter);

    const TABS = [
        { id: "all",       label: "Všetky",     count: counts.all },
        { id: "pending",   label: "Čakajúce",   count: counts.pending,   accent: true },
        { id: "confirmed", label: "Potvrdené",  count: counts.confirmed },
        { id: "cancelled", label: "Zamietnuté", count: counts.cancelled },
    ];

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-nova-cyan mb-1">/ Rezervácie</div>
                    <h1 className="font-heading text-2xl font-black tracking-tight">
                        Rezervácie prenájmu
                        {newCount > 0 && (
                            <span className="ml-3 inline-flex items-center justify-center min-w-[22px] h-5.5 px-1.5 rounded-full bg-nova-cyan text-black text-[10px] font-black">
                                {newCount} nová{newCount > 1 ? "" : ""}
                            </span>
                        )}
                    </h1>
                    <p className="text-zinc-500 text-xs mt-0.5">{counts.all} rezervácií celkom</p>
                </div>
            </div>

            {/* Filter tabs */}
            <div className="flex gap-0.5 border-b border-white/8 mb-5 overflow-x-auto">
                {TABS.map(t => (
                    <button key={t.id} onClick={() => setFilter(t.id)}
                        className={`px-4 py-2.5 text-xs font-medium whitespace-nowrap border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${
                            filter === t.id ? "text-white border-nova-cyan" : "text-zinc-500 border-transparent hover:text-zinc-300"
                        }`}>
                        {t.label}
                        {t.count > 0 && (
                            <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded-full ${
                                filter === t.id
                                    ? "bg-nova-cyan/20 text-nova-cyan"
                                    : t.accent && t.count > 0
                                        ? "bg-amber-400/15 text-amber-400"
                                        : "bg-white/5 text-zinc-600"
                            }`}>{t.count}</span>
                        )}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="space-y-2 animate-pulse">
                    {[1,2,3].map(n => <div key={n} className="h-20 bg-nova-surface/30 rounded-xl" />)}
                </div>
            ) : filtered.length === 0 ? (
                <div className="text-center py-16 text-zinc-600 text-sm">
                    Žiadne rezervácie.
                </div>
            ) : (
                <div className="space-y-2">
                    {filtered.map(b => (
                        <BookingCard key={b.id} booking={b} onStatusChange={handleStatusChange} onDelete={handleDelete} />
                    ))}
                </div>
            )}
        </div>
    );
}
